import PDFFooter from "@components/pdf-partials/PDFFooter";
import PDFPropertyHeader from "@components/pdf-partials/PDFPropertyHeader";
import { hasAddressLine2AsCustom, renderUrlOrValue } from "@shared/utils";
import SurveyPDFPage from "@survey-export/SurveyPDFPage";
import { PageData } from "@survey-preview/utils/heightPagination";
import { getPropertyDisplayIndex } from "@survey-preview/utils/propertyIndexUtils";
import { DetailedSurvey } from "@survey-types/survey.core.types";
import { GetMediaByIdsResponse } from "@survey-types/survey.media.types";
import { Property } from "@survey-types/survey.property.types";
import PropertyFeatureImage from "./PropertyFeatureImage";

interface PropertyPageProps {
  page: PageData;
  pageIndex: number;
  property: Property;
  propertyIndex: number;
  survey: DetailedSurvey | null;
  mediaResponse?: GetMediaByIdsResponse | undefined;
  mode?: string;
}

const PropertyPage = ({
  page,
  pageIndex,
  property,
  propertyIndex,
  survey,
  mode,
}: PropertyPageProps) => {
  const hasAddressLine2Custom = survey ? hasAddressLine2AsCustom(survey) : true;

  return (
    <SurveyPDFPage
      key={`property-${propertyIndex}-page-${pageIndex}`}
      mode={mode}
    >
      <div className="grid grid-rows-[auto_1fr_auto] h-full w-full bg-white">
        {/* Header - Only on first page */}
        {pageIndex === 0 && (
          <PDFPropertyHeader
            property={property}
            index={getPropertyDisplayIndex(property, survey)}
            showCurrentLocationIcon={survey?.showCurrentLocationPD}
            fieldRules={survey?.surveyPropertyFieldRule?.propertyFieldRuleList}
            hasAddressLine2Custom={hasAddressLine2Custom}
          />
        )}

        {/* Main content area - calculated height to leave room for footer */}
        <div className="grid grid-cols-1 px-8 py-4 lg:grid-cols-2 print:grid-cols-2 gap-4 h-full w-full">
          {/* Left Column - Comments and Amenities */}
          <div className="space-y-4">
            {/* Property Feature Image - Only on first page */}
            {pageIndex === 0 && (
              <PropertyFeatureImage
                propertyId={property.propertyId}
                size="standard"
                order={getPropertyDisplayIndex(property, survey)}
                asBackground={true}
              />
            )}

            {/* Dynamic left column content */}
            {page.leftContent.map((item, itemIndex) => (
              <div key={`${item.type}-${itemIndex}`} className="pr-4 py-2">
                <h3 className="mb-2 font-normal text-primary">{item.title}</h3>
                <p className="whitespace-pre-line text-xs text-data wrap-break-word">
                  {item.content}
                </p>
              </div>
            ))}
          </div>

          {/* Right Column - Property Fields (distributed across pages) */}
          <div className="w-full">
            <div className="overflow-hidden rounded-lg w-full">
              <table className="w-full table-fixed">
                <tbody className="bg-white">
                  {page.propertyFields.map((field, fieldIndex) => (
                    <tr
                      key={fieldIndex}
                      className={
                        fieldIndex % 2 === 0
                          ? "bg-white"
                          : "bg-secondary-lightest"
                      }
                    >
                      <td className="px-2 py-2 w-1/2 text-xs text-data align-top">
                        {field.label}
                      </td>
                      <td
                        className="px-2 py-2 w-1/2 text-xs text-data align-top"
                        style={{
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {renderUrlOrValue(field.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer - Always at bottom with fixed height and absolute positioning */}
        <PDFFooter />
      </div>
    </SurveyPDFPage>
  );
};

export default PropertyPage;
